import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  useColorMode,
  Link,
  Divider,
  Image,
  Circle,
  Spinner,
  Center,
  useToast,
  SimpleGrid,
  Flex,
} from "@chakra-ui/react";
import { SEOHead } from "../seometadata/components/SEOHead";

// Countdown / count-up timer hook
const usePartyTimer = (targetDate: Date) => {
  const [timer, setTimer] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isPartyTime: false,
    intensity: 0, // 0-1 over 12 hours
  });

  useEffect(() => {
    const calculate = () => {
      const now = new Date().getTime();
      const target = targetDate.getTime();
      const difference = target - now;

      if (difference > 0) {
        // Counting DOWN
        setTimer({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60),
          isPartyTime: false,
          intensity: 0,
        });
      } else {
        // Counting UP — party is live!
        const elapsed = Math.abs(difference);
        const hoursElapsed = elapsed / (1000 * 60 * 60);
        // Intensity ramps 0→1 over 12 hours, capped at 1
        const intensity = Math.min(hoursElapsed / 12, 1);
        setTimer({
          days: Math.floor(elapsed / (1000 * 60 * 60 * 24)),
          hours: Math.floor((elapsed / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((elapsed / (1000 * 60)) % 60),
          seconds: Math.floor((elapsed / 1000) % 60),
          isPartyTime: true,
          intensity,
        });
      }
    };

    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timer;
};

// Alarming milestone messages based on how long the party's been going
const getPartyMilestone = (hours: number, minutes: number) => {
  const totalMins = hours * 60 + minutes;
  if (totalMins < 5) return "🚨 DEFCON 40 — TOM IS OFFICIALLY OLD 🚨";
  if (totalMins < 30) return "⚠️ ALERT: OLD MAN'S PARTY NOW ACTIVE ⚠️";
  if (totalMins < 60) return "🔴 TOM'S YOUTH HAS LEFT THE BUILDING 🔴";
  if (totalMins < 120) return "⚠️ 2HRS IN — TOM HASN'T MENTIONED HIS KNEES YET... WAIT... NEVERMIND ⚠️";
  if (totalMins < 150) return "🚨 FOOD INBOUND — TOM NEEDS FUEL TO SURVIVE ⚠️";
  if (totalMins < 240) return "🔴 FOOD DEPLOYED! EAT BEFORE TOM FALLS ASLEEP STANDING UP 🔴";
  if (totalMins < 360) return "⚠️ STILL OPERATIONAL?! MEDICS ON STANDBY ⚠️";
  if (totalMins < 480) return "🚨 TOM'S BACK HAS FILED A FORMAL COMPLAINT 🚨";
  if (totalMins < 600) return "🔴 PAST BEDTIME — THIS IS NOW A HOSTAGE SITUATION 🔴";
  return "⚠️ SOMEONE DO A WELFARE CHECK ON TOM ⚠️";
};

import { GET_BDAY_GUEST_BY_SLUG, SUBMIT_BDAY_RSVP } from "./queries";

type RsvpStatus = "PENDING" | "ATTENDING" | "NOT_ATTENDING" | "MAYBE";

// ============================================================
// CHANGE THIS TIME TO TEST DIFFERENT PARTY INTENSITY LEVELS
// The further back in time, the more hours "elapsed", the redder/crazier the page
//
//   '2026-03-14T08:00:00+10:00'  ← just started (0% red)
//   '2026-03-14T06:00:00+10:00'  ← 2hrs in (~17% red)
//   '2026-03-14T04:00:00+10:00'  ← 4hrs in (~33% red, skulls appear)
//   '2026-03-14T02:00:00+10:00'  ← 6hrs in (~50% red, extra confetti)
//   '2026-03-14T00:00:00+10:00'  ← 8hrs in (~67% red, wheelchairs)
//   '2026-03-13T22:00:00+10:00'  ← 10hrs in (~83% red)
//   '2026-03-13T20:00:00+10:00'  ← 12hrs in (100% FULL APOCALYPSE)
//
// REVERT TO '2026-03-14T14:00:00+10:00' BEFORE GOING LIVE (2pm party start)
// ============================================================
const PARTY_DATE = new Date('2026-03-14T14:00:00+10:00');

const BdayRsvpPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { colorMode } = useColorMode();
  const toast = useToast();

  const [selectedStatus, setSelectedStatus] = useState<RsvpStatus | null>(null);
  const [plusOnes, setPlusOnes] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Countdown to the party / party timer - March 14, 2026 at 2pm AEST
  const partyTimer = usePartyTimer(PARTY_DATE);

  // Elegant boutique colour palette — shifts red with party intensity
  const i = partyTimer.intensity; // 0 (party start) → 1 (12hrs in)
  const champagne = "#F7E7CE";
  const softGold = "#D4AF37";
  const warmWhite = "#FAF9F6";
  const deepCharcoal = "#1C1C1C";

  // Lerp helper: blend between two 0-255 values
  const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);

  // Background: warm white → increasingly red
  const bgR = lerp(250, 180, i); // FAF9F6 → deep red
  const bgG = lerp(249, 40, i);
  const bgB = lerp(246, 40, i);
  const bgEnd = partyTimer.isPartyTime
    ? `rgba(${lerp(247, 120, i)}, ${lerp(231, 20, i)}, ${lerp(206, 20, i)}, 0.25)`
    : `${champagne}40`;

  const bgGradient = partyTimer.isPartyTime
    ? `linear-gradient(180deg, rgb(${bgR}, ${bgG}, ${bgB}) 0%, ${bgEnd} 100%)`
    : (colorMode === 'light'
      ? `linear-gradient(180deg, ${warmWhite} 0%, ${champagne}40 100%)`
      : `linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)`);

  // Card bg: white → pinkish/reddish tint
  const cardR = lerp(255, 255, i);
  const cardG = lerp(255, 220 - Math.round(i * 80), i);
  const cardB = lerp(255, 220 - Math.round(i * 80), i);
  const cardBg = partyTimer.isPartyTime
    ? `rgba(${cardR}, ${cardG}, ${cardB}, 1)`
    : (colorMode === 'light' ? "rgba(255, 255, 255, 1)" : "rgba(30, 30, 46, 1)");

  const textPrimary = colorMode === 'light' ? deepCharcoal : "#F5F5F5";
  const textSecondary = colorMode === 'light' ? "#666666" : "#B0B0B0";

  // Accent: gold → red as intensity increases
  const acR = lerp(212, 220, i);
  const acG = lerp(175, 50, i);
  const acB = lerp(55, 50, i);
  const accentColor = partyTimer.isPartyTime
    ? `rgb(${acR}, ${acG}, ${acB})`
    : (colorMode === 'light' ? softGold : "#E8D5A3");

  const { data, loading, error } = useQuery(GET_BDAY_GUEST_BY_SLUG, {
    variables: { slug },
    skip: !slug,
  });

  const [submitRsvp, { loading: submitting }] = useMutation(SUBMIT_BDAY_RSVP, {
    onCompleted: () => {
      setHasSubmitted(true);
      toast({
        title: "RSVP Submitted!",
        description: "Thanks for letting us know!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const guest = data?.bdayGuestBySlug;

  // Set initial values if guest has already responded
  useEffect(() => {
    if (guest?.rsvpStatus && guest.rsvpStatus !== "PENDING") {
      setSelectedStatus(guest.rsvpStatus);
      setPlusOnes(guest.plusOnes || 0);
      if (guest.rsvpRespondedAt) {
        setHasSubmitted(true);
      }
    }
  }, [guest]);

  const handleSubmit = () => {
    if (!selectedStatus || !slug) return;

    submitRsvp({
      variables: {
        slug,
        input: {
          rsvpStatus: selectedStatus,
          plusOnes: selectedStatus === "ATTENDING" ? plusOnes : 0,
        },
      },
    });
  };

  // Loading state
  if (loading) {
    return (
      <Box bg={bgGradient} minHeight="100vh">
        <Center h="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color={accentColor} thickness="3px" />
            <Text color={textSecondary}>Loading your invite...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  // Not found state
  if (error || !guest) {
    return (
      <Box bg={bgGradient} minHeight="100vh">
        <Center h="100vh">
          <VStack spacing={4}>
            <Text fontSize="6xl">🎂</Text>
            <Heading size="lg" color={textPrimary}>Invite not found</Heading>
            <Text color={textSecondary}>This invite link doesn't seem to be valid.</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box
      bg={bgGradient}
      minHeight="100vh"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: colorMode === 'light'
          ? `radial-gradient(60% 60% at 80% 20%, ${champagne}40 0%, transparent 50%)`
          : `radial-gradient(60% 60% at 80% 20%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)`,
        pointerEvents: 'none',
        zIndex: 0,
      }}
      sx={{
        '@keyframes borderShimmer': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' }
        },
        '@keyframes gentleBounce': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-3px)' }
        },
        '@keyframes confettiFall': {
          '0%': { transform: 'translateY(-100vh) rotate(0deg)', opacity: 1 },
          '100%': { transform: 'translateY(100vh) rotate(720deg)', opacity: 0 }
        },
        '@keyframes confettiFall2': {
          '0%': { transform: 'translateY(-100vh) rotate(0deg) translateX(0)', opacity: 1 },
          '100%': { transform: 'translateY(100vh) rotate(-540deg) translateX(80px)', opacity: 0 }
        },
        '@keyframes confettiFall3': {
          '0%': { transform: 'translateY(-100vh) rotate(0deg) translateX(0)', opacity: 1 },
          '100%': { transform: 'translateY(100vh) rotate(360deg) translateX(-60px)', opacity: 0 }
        },
        '@keyframes floatEmoji': {
          '0%': { transform: 'translateY(100vh) scale(0)', opacity: 0 },
          '10%': { opacity: 1, transform: 'translateY(80vh) scale(1)' },
          '90%': { opacity: 1, transform: 'translateY(-10vh) scale(1)' },
          '100%': { transform: 'translateY(-20vh) scale(0)', opacity: 0 }
        },
        '@keyframes pulseGlow': {
          '0%, 100%': { boxShadow: '0 0 20px rgba(72, 187, 120, 0.3), 0 0 60px rgba(72, 187, 120, 0.1)' },
          '50%': { boxShadow: '0 0 40px rgba(72, 187, 120, 0.5), 0 0 80px rgba(72, 187, 120, 0.2)' }
        },
        '@keyframes disco': {
          '0%': { backgroundColor: 'rgba(255, 107, 107, 0.08)' },
          '16%': { backgroundColor: 'rgba(255, 217, 61, 0.08)' },
          '33%': { backgroundColor: 'rgba(72, 187, 120, 0.08)' },
          '50%': { backgroundColor: 'rgba(66, 153, 225, 0.08)' },
          '66%': { backgroundColor: 'rgba(159, 122, 234, 0.08)' },
          '83%': { backgroundColor: 'rgba(237, 100, 166, 0.08)' },
          '100%': { backgroundColor: 'rgba(255, 107, 107, 0.08)' }
        },
        '@keyframes wobble': {
          '0%, 100%': { transform: 'rotate(0deg)' },
          '25%': { transform: 'rotate(0.5deg)' },
          '75%': { transform: 'rotate(-0.5deg)' }
        },
        '@keyframes spin40': {
          '0%': { transform: 'rotateY(0deg)' },
          '100%': { transform: 'rotateY(360deg)' }
        },
        '@keyframes rainbowText': {
          '0%': { color: '#FF6B6B' },
          '16%': { color: '#FFD93D' },
          '33%': { color: '#6BCB77' },
          '50%': { color: '#4D96FF' },
          '66%': { color: '#9F7AEA' },
          '83%': { color: '#ED64A6' },
          '100%': { color: '#FF6B6B' }
        },
        '@keyframes marquee': {
          '0%': { transform: 'translateX(100%)' },
          '100%': { transform: 'translateX(-100%)' }
        },
        '@keyframes bounce': {
          '0%, 100%': { transform: 'translateY(0) scale(1)' },
          '50%': { transform: 'translateY(-10px) scale(1.05)' }
        },
        '@keyframes riseUp': {
          '0%': { transform: 'translateY(0) scale(1)', opacity: 1 },
          '50%': { opacity: 0.8 },
          '100%': { transform: 'translateY(-600px) scale(0.5)', opacity: 0 }
        },
        '@keyframes riseBubble': {
          '0%': { transform: 'translateY(0) translateX(0) scale(1)', opacity: 0.8 },
          '25%': { transform: 'translateY(-150px) translateX(10px) scale(0.9)' },
          '50%': { transform: 'translateY(-300px) translateX(-8px) scale(0.8)', opacity: 0.6 },
          '75%': { transform: 'translateY(-450px) translateX(12px) scale(0.7)' },
          '100%': { transform: 'translateY(-600px) translateX(-5px) scale(0.5)', opacity: 0 }
        },
        '@keyframes streamerRise': {
          '0%': { transform: 'translateY(0) rotate(0deg) scaleY(1)', opacity: 0.9 },
          '100%': { transform: 'translateY(-500px) rotate(180deg) scaleY(2)', opacity: 0 }
        },
        '@keyframes flashRed': {
          '0%, 49%': { opacity: 1 },
          '50%, 100%': { opacity: 0 }
        },
        '@keyframes flashBlue': {
          '0%, 49%': { opacity: 0 },
          '50%, 100%': { opacity: 1 }
        },
        '@keyframes warningPulse': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.7, transform: 'scale(1.02)' }
        },
        '@keyframes sirenGlow': {
          '0%': { boxShadow: '0 0 20px rgba(255,0,0,0.4), inset 0 0 20px rgba(255,0,0,0.1)' },
          '50%': { boxShadow: '0 0 20px rgba(0,100,255,0.4), inset 0 0 20px rgba(0,100,255,0.1)' },
          '100%': { boxShadow: '0 0 20px rgba(255,0,0,0.4), inset 0 0 20px rgba(255,0,0,0.1)' }
        },
        '@keyframes sparkle': {
          '0%': { transform: 'translateX(-200px) translateY(-100px)', opacity: 0 },
          '5%': { opacity: 0.4 },
          '25%': { transform: 'translateX(25vw) translateY(25vh)', opacity: 0.8 },
          '50%': { transform: 'translateX(50vw) translateY(50vh)', opacity: 1 },
          '75%': { transform: 'translateX(75vw) translateY(75vh)', opacity: 0.8 },
          '95%': { opacity: 0.4 },
          '100%': { transform: 'translateX(calc(100vw + 200px)) translateY(100vh)', opacity: 0 }
        },
        '@keyframes sparkle-slow': {
          '0%': { transform: 'translateX(-250px) translateY(-100px)', opacity: 0 },
          '5%': { opacity: 0.3 },
          '30%': { transform: 'translateX(35vw) translateY(30vh)', opacity: 0.6 },
          '60%': { transform: 'translateX(65vw) translateY(60vh)', opacity: 0.8 },
          '95%': { opacity: 0.3 },
          '100%': { transform: 'translateX(calc(100vw + 250px)) translateY(100vh)', opacity: 0 }
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-10%',
          width: '120%',
          height: '100%',
          backgroundImage: colorMode === 'light'
            ? `
              radial-gradient(3px 3px at 10% 10%, rgba(212,175,55,0.6) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 30%, rgba(212,175,55,0.5) 50%, transparent 50%),
              radial-gradient(2px 2px at 15% 50%, rgba(212,175,55,0.7) 50%, transparent 50%),
              radial-gradient(5px 5px at 25% 20%, rgba(212,175,55,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 30% 60%, rgba(212,175,55,0.6) 50%, transparent 50%),
              radial-gradient(6px 6px at 35% 40%, rgba(212,175,55,0.3) 50%, transparent 50%),
              radial-gradient(4px 4px at 40% 70%, rgba(212,175,55,0.5) 50%, transparent 50%),
              radial-gradient(2px 2px at 45% 15%, rgba(212,175,55,0.8) 50%, transparent 50%),
              radial-gradient(5px 5px at 50% 80%, rgba(212,175,55,0.4) 50%, transparent 50%)
            `
            : `
              radial-gradient(3px 3px at 10% 10%, rgba(232,213,163,0.5) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 30%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(2px 2px at 15% 50%, rgba(232,213,163,0.6) 50%, transparent 50%),
              radial-gradient(5px 5px at 25% 20%, rgba(232,213,163,0.3) 50%, transparent 50%),
              radial-gradient(3px 3px at 30% 60%, rgba(232,213,163,0.5) 50%, transparent 50%),
              radial-gradient(6px 6px at 35% 40%, rgba(232,213,163,0.25) 50%, transparent 50%),
              radial-gradient(4px 4px at 40% 70%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(2px 2px at 45% 15%, rgba(232,213,163,0.7) 50%, transparent 50%),
              radial-gradient(5px 5px at 50% 80%, rgba(232,213,163,0.35) 50%, transparent 50%)
            `,
          backgroundSize: '400px 400px, 500px 500px, 350px 350px, 450px 450px, 550px 550px, 600px 600px, 480px 480px, 420px 420px, 520px 520px',
          backgroundPosition: '0 0, 80px 60px, 160px 40px, 240px 100px, 320px 160px, 400px 80px, 480px 200px, 560px 20px, 640px 180px',
          animation: 'sparkle 25s linear infinite',
          pointerEvents: 'none',
          zIndex: 1,
        }
      }}
    >
      <SEOHead
        pagePath={`/40years/${slug}`}
        fallbackTitle={`${guest.firstName}'s Invite - Tom's 40th`}
        fallbackDescription={`${guest.firstName} is invited to Tom's 40th Birthday. A poolside celebration on Saturday 14th March 2026.`}
      />

      {/* Secondary sparkle layer */}
      <Box
        position="absolute"
        top={0}
        left="-20%"
        width="140%"
        height="100%"
        opacity={0.5}
        pointerEvents="none"
        zIndex={1}
        sx={{
          backgroundImage: colorMode === 'light'
            ? `
              radial-gradient(5px 5px at 5% 15%, rgba(212,175,55,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 15% 35%, rgba(212,175,55,0.5) 50%, transparent 50%),
              radial-gradient(7px 7px at 10% 55%, rgba(212,175,55,0.3) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 25%, rgba(212,175,55,0.6) 50%, transparent 50%),
              radial-gradient(8px 8px at 25% 45%, rgba(212,175,55,0.25) 50%, transparent 50%),
              radial-gradient(6px 6px at 30% 65%, rgba(212,175,55,0.45) 50%, transparent 50%),
              radial-gradient(3px 3px at 35% 20%, rgba(212,175,55,0.7) 50%, transparent 50%)
            `
            : `
              radial-gradient(5px 5px at 5% 15%, rgba(232,213,163,0.35) 50%, transparent 50%),
              radial-gradient(3px 3px at 15% 35%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(7px 7px at 10% 55%, rgba(232,213,163,0.25) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 25%, rgba(232,213,163,0.5) 50%, transparent 50%),
              radial-gradient(8px 8px at 25% 45%, rgba(232,213,163,0.2) 50%, transparent 50%),
              radial-gradient(6px 6px at 30% 65%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 35% 20%, rgba(232,213,163,0.6) 50%, transparent 50%)
            `,
          backgroundSize: '600px 600px, 700px 700px, 550px 550px, 650px 650px, 750px 750px, 680px 680px, 580px 580px',
          backgroundPosition: '0 0, 120px 140px, 240px 80px, 360px 200px, 480px 280px, 600px 120px, 720px 40px',
          animation: 'sparkle-slow 35s linear infinite',
        }}
      />

      {/* Red vignette that intensifies over time */}
      {partyTimer.isPartyTime && (
        <Box
          position="absolute"
          inset={0}
          pointerEvents="none"
          zIndex={1}
          bg={`radial-gradient(ellipse at center, transparent 30%, rgba(180, 20, 20, ${(i * 0.35).toFixed(2)}) 100%)`}
        />
      )}

      {/* Party mode overlays */}
      {partyTimer.isPartyTime && (
        <>
          {/* Subtle disco colour wash */}
          <Box
            position="absolute"
            inset={0}
            animation="disco 8s ease-in-out infinite"
            pointerEvents="none"
            zIndex={1}
          />

          {/* Confetti strips - multi-coloured rectangles falling */}
          {[
            { left: '5%', color: '#FF6B6B', delay: '0s', duration: '4s', anim: 'confettiFall' },
            { left: '15%', color: '#FFD93D', delay: '0.5s', duration: '5s', anim: 'confettiFall2' },
            { left: '25%', color: '#6BCB77', delay: '1s', duration: '4.5s', anim: 'confettiFall3' },
            { left: '35%', color: '#4D96FF', delay: '1.5s', duration: '5.5s', anim: 'confettiFall' },
            { left: '45%', color: '#9F7AEA', delay: '0.3s', duration: '4.2s', anim: 'confettiFall2' },
            { left: '55%', color: '#ED64A6', delay: '2s', duration: '5.2s', anim: 'confettiFall3' },
            { left: '65%', color: '#F6AD55', delay: '0.8s', duration: '4.8s', anim: 'confettiFall' },
            { left: '75%', color: '#68D391', delay: '1.2s', duration: '5.3s', anim: 'confettiFall2' },
            { left: '85%', color: '#FC8181', delay: '1.8s', duration: '4.6s', anim: 'confettiFall3' },
            { left: '92%', color: '#63B3ED', delay: '0.6s', duration: '5.1s', anim: 'confettiFall' },
            { left: '10%', color: '#B794F4', delay: '2.5s', duration: '4.3s', anim: 'confettiFall3' },
            { left: '50%', color: '#FEB2B2', delay: '3s', duration: '5.4s', anim: 'confettiFall2' },
            { left: '70%', color: '#FEFCBF', delay: '1.7s', duration: '4.7s', anim: 'confettiFall' },
            { left: '30%', color: '#C6F6D5', delay: '2.2s', duration: '5.6s', anim: 'confettiFall3' },
            { left: '80%', color: '#E9D8FD', delay: '0.4s', duration: '4.4s', anim: 'confettiFall2' },
          ].map((c, i) => (
            <Box
              key={`confetti-${i}`}
              position="absolute"
              left={c.left}
              top="-20px"
              width={`${6 + (i % 3) * 3}px`}
              height={`${12 + (i % 4) * 4}px`}
              bg={c.color}
              borderRadius="1px"
              opacity={0.7}
              pointerEvents="none"
              zIndex={3}
              sx={{
                animation: `${c.anim} ${c.duration} linear ${c.delay} infinite`,
              }}
            />
          ))}

          {/* Extra red confetti wave — appears after 50% intensity */}
          {i > 0.5 && [
            { left: '3%', delay: '0.2s', duration: '3s', anim: 'confettiFall' },
            { left: '18%', delay: '0.7s', duration: '3.5s', anim: 'confettiFall2' },
            { left: '33%', delay: '0.1s', duration: '2.8s', anim: 'confettiFall3' },
            { left: '48%', delay: '0.9s', duration: '3.2s', anim: 'confettiFall' },
            { left: '58%', delay: '0.4s', duration: '2.5s', anim: 'confettiFall2' },
            { left: '68%', delay: '1.1s', duration: '3.3s', anim: 'confettiFall3' },
            { left: '78%', delay: '0.6s', duration: '2.7s', anim: 'confettiFall' },
            { left: '90%', delay: '0.3s', duration: '3.1s', anim: 'confettiFall2' },
            { left: '12%', delay: '1.4s', duration: '2.6s', anim: 'confettiFall3' },
            { left: '42%', delay: '0.8s', duration: '3.4s', anim: 'confettiFall' },
          ].map((c, idx) => (
            <Box
              key={`red-confetti-${idx}`}
              position="absolute"
              left={c.left}
              top="-20px"
              width={`${8 + (idx % 3) * 4}px`}
              height={`${14 + (idx % 4) * 5}px`}
              bg={`rgba(220, ${lerp(60, 0, i)}, ${lerp(60, 0, i)}, ${(0.5 + i * 0.4).toFixed(1)})`}
              borderRadius="1px"
              pointerEvents="none"
              zIndex={3}
              sx={{
                animation: `${c.anim} ${c.duration} linear ${c.delay} infinite`,
              }}
            />
          ))}

          {/* Floating party emoji */}
          {[
            { emoji: '🎉', left: '8%', delay: '0s', duration: '6s' },
            { emoji: '🥳', left: '22%', delay: '2s', duration: '7s' },
            { emoji: '🍻', left: '40%', delay: '4s', duration: '8s' },
            { emoji: '🎂', left: '58%', delay: '1s', duration: '7.5s' },
            { emoji: '🔥', left: '75%', delay: '3s', duration: '6.5s' },
            { emoji: '🎈', left: '88%', delay: '5s', duration: '7s' },
            { emoji: '🥂', left: '50%', delay: '6s', duration: '8.5s' },
            { emoji: '🎊', left: '15%', delay: '7s', duration: '6s' },
            // Alarming emoji appear after 30% intensity
            ...(i > 0.3 ? [
              { emoji: '🚨', left: '5%', delay: '1.5s', duration: '5s' },
              { emoji: '💀', left: '35%', delay: '3.5s', duration: '6s' },
              { emoji: '🚑', left: '62%', delay: '0.5s', duration: '7s' },
              { emoji: '⚰️', left: '82%', delay: '4.5s', duration: '5.5s' },
            ] : []),
            // Full chaos after 70%
            ...(i > 0.7 ? [
              { emoji: '👴', left: '18%', delay: '2.5s', duration: '4.5s' },
              { emoji: '🦽', left: '45%', delay: '5.5s', duration: '5s' },
              { emoji: '📟', left: '72%', delay: '1s', duration: '6s' },
              { emoji: '🪦', left: '90%', delay: '3s', duration: '5.5s' },
            ] : []),
          ].map((e, idx) => (
            <Text
              key={`emoji-${idx}`}
              position="absolute"
              left={e.left}
              bottom="0"
              fontSize={{ base: "2xl", md: "3xl" }}
              pointerEvents="none"
              zIndex={3}
              sx={{
                animation: `floatEmoji ${e.duration} ease-in-out ${e.delay} infinite`,
              }}
            >
              {e.emoji}
            </Text>
          ))}
        </>
      )}

      <Container maxW="container.sm" py={{ base: 8, md: 16 }} px={{ base: 4, md: 8 }} position="relative" zIndex={2}>
        <VStack spacing={0} align="stretch">

          {/* EMERGENCY BROADCAST during party mode */}
          {partyTimer.isPartyTime && (
            <>
              {/* Flashing police lights */}
              <HStack spacing={0} mb={2} borderRadius="lg" overflow="hidden">
                <Box
                  flex={1} h="6px" bg="red.500"
                  sx={{ animation: 'flashRed 0.8s linear infinite' }}
                />
                <Box
                  flex={1} h="6px" bg="blue.500"
                  sx={{ animation: 'flashBlue 0.8s linear infinite' }}
                />
                <Box
                  flex={1} h="6px" bg="red.500"
                  sx={{ animation: 'flashRed 0.8s linear infinite', animationDelay: '0.4s' }}
                />
                <Box
                  flex={1} h="6px" bg="blue.500"
                  sx={{ animation: 'flashBlue 0.8s linear infinite', animationDelay: '0.4s' }}
                />
              </HStack>

              {/* Emergency broadcast banner */}
              <Box
                overflow="hidden"
                bg="red.600"
                borderRadius="md"
                mb={2}
                py={2}
                position="relative"
                sx={{ animation: 'warningPulse 2s ease-in-out infinite' }}
              >
                <Text
                  fontWeight="900"
                  color="white"
                  fontSize="xs"
                  textAlign="center"
                  textTransform="uppercase"
                  letterSpacing="0.3em"
                  mb={1}
                >
                  ⚠️ EMERGENCY BROADCAST ⚠️
                </Text>
                <Text
                  fontWeight="bold"
                  color="yellow.200"
                  fontSize="sm"
                  whiteSpace="nowrap"
                  sx={{ animation: 'marquee 12s linear infinite' }}
                >
                  🚨 ALERT: LOCAL MAN TURNS 40 — YOUTH OFFICIALLY DECEASED — AREA RESIDENTS URGED TO BRING DRINKS — SUSPECT LAST SEEN NEAR BBQ — HIS KNEES MAKE SOUNDS NOW — REPEAT: HIS KNEES MAKE SOUNDS — FOOD EXPECTED AT 4PM — SEND REINFORCEMENTS 🚨
                </Text>
              </Box>

              {/* Bottom flashing lights */}
              <HStack spacing={0} mb={4} borderRadius="lg" overflow="hidden">
                <Box
                  flex={1} h="6px" bg="blue.500"
                  sx={{ animation: 'flashBlue 0.8s linear infinite' }}
                />
                <Box
                  flex={1} h="6px" bg="red.500"
                  sx={{ animation: 'flashRed 0.8s linear infinite' }}
                />
                <Box
                  flex={1} h="6px" bg="blue.500"
                  sx={{ animation: 'flashBlue 0.8s linear infinite', animationDelay: '0.4s' }}
                />
                <Box
                  flex={1} h="6px" bg="red.500"
                  sx={{ animation: 'flashRed 0.8s linear infinite', animationDelay: '0.4s' }}
                />
              </HStack>
            </>
          )}

          {/* Main Invitation Card with rotating shimmer border */}
          <Box
            position="relative"
            borderRadius="3xl"
            p="1.5px"
            overflow="hidden"
            shadow={partyTimer.isPartyTime ? "none" : "2xl"}
            sx={partyTimer.isPartyTime ? {
              animation: 'pulseGlow 3s ease-in-out infinite, wobble 2s ease-in-out infinite',
            } : {}}
          >
            {/* Rotating gradient border - larger arc to reach all edges */}
            <Box
              position="absolute"
              top="-50%"
              left="-50%"
              width="200%"
              height="200%"
              bg={`conic-gradient(from 0deg, transparent 0deg, transparent 30deg, ${accentColor} 70deg, ${accentColor} 110deg, transparent 150deg, transparent 360deg)`}
              animation="borderShimmer 4s linear infinite"
              sx={{
                transformOrigin: 'center center',
              }}
            />
            {/* Static subtle border underneath */}
            <Box
              position="absolute"
              inset={0}
              borderRadius="3xl"
              bg={colorMode === 'light' ? "rgba(212, 175, 55, 0.15)" : "rgba(232, 213, 163, 0.1)"}
            />
            {/* Card content */}
            <Box
              bg={cardBg}
              borderRadius="calc(1.5rem - 1.5px)"
              position="relative"
              zIndex={1}
              overflow="hidden"
            >
            {/* Champagne bubbles & streamers rising from bottom of card */}
            {partyTimer.isPartyTime && (
              <>
                {/* Champagne bubbles */}
                {[
                  { left: '10%', size: '8px', delay: '0s', duration: '4s' },
                  { left: '20%', size: '5px', delay: '1.5s', duration: '5s' },
                  { left: '35%', size: '10px', delay: '0.5s', duration: '4.5s' },
                  { left: '50%', size: '6px', delay: '2s', duration: '5.5s' },
                  { left: '65%', size: '9px', delay: '1s', duration: '4.2s' },
                  { left: '78%', size: '7px', delay: '3s', duration: '5.2s' },
                  { left: '88%', size: '5px', delay: '0.8s', duration: '4.8s' },
                  { left: '15%', size: '6px', delay: '2.5s', duration: '5.3s' },
                  { left: '42%', size: '8px', delay: '3.5s', duration: '4.6s' },
                  { left: '72%', size: '11px', delay: '1.2s', duration: '5.1s' },
                  { left: '55%', size: '4px', delay: '4s', duration: '4.3s' },
                  { left: '30%', size: '7px', delay: '0.3s', duration: '5.7s' },
                ].map((b, i) => (
                  <Box
                    key={`bubble-${i}`}
                    position="absolute"
                    bottom="10px"
                    left={b.left}
                    width={b.size}
                    height={b.size}
                    borderRadius="full"
                    bg="rgba(212, 175, 55, 0.4)"
                    border="1px solid rgba(212, 175, 55, 0.6)"
                    pointerEvents="none"
                    zIndex={2}
                    sx={{
                      animation: `riseBubble ${b.duration} ease-out ${b.delay} infinite`,
                    }}
                  />
                ))}

                {/* Streamers / ribbons rising */}
                {[
                  { left: '8%', color: '#FF6B6B', delay: '0s', duration: '6s' },
                  { left: '25%', color: '#FFD93D', delay: '2s', duration: '7s' },
                  { left: '45%', color: '#6BCB77', delay: '1s', duration: '5.5s' },
                  { left: '60%', color: '#4D96FF', delay: '3s', duration: '6.5s' },
                  { left: '80%', color: '#9F7AEA', delay: '0.5s', duration: '7.5s' },
                  { left: '92%', color: '#ED64A6', delay: '4s', duration: '6s' },
                  { left: '38%', color: '#F6AD55', delay: '1.5s', duration: '7s' },
                  { left: '70%', color: '#63B3ED', delay: '2.5s', duration: '5.8s' },
                ].map((s, i) => (
                  <Box
                    key={`streamer-${i}`}
                    position="absolute"
                    bottom="0"
                    left={s.left}
                    width="3px"
                    height="30px"
                    bg={s.color}
                    borderRadius="full"
                    opacity={0.6}
                    pointerEvents="none"
                    zIndex={2}
                    sx={{
                      animation: `streamerRise ${s.duration} ease-out ${s.delay} infinite`,
                    }}
                  />
                ))}

                {/* Rising party emoji from bottom of card */}
                {[
                  { emoji: '🍾', left: '12%', delay: '0s', duration: '5s' },
                  { emoji: '🥂', left: '30%', delay: '2.5s', duration: '6s' },
                  { emoji: '✨', left: '50%', delay: '1s', duration: '5.5s' },
                  { emoji: '🎉', left: '70%', delay: '3.5s', duration: '6.5s' },
                  { emoji: '🍾', left: '85%', delay: '1.5s', duration: '5.8s' },
                ].map((e, i) => (
                  <Text
                    key={`card-emoji-${i}`}
                    position="absolute"
                    bottom="5px"
                    left={e.left}
                    fontSize="xl"
                    pointerEvents="none"
                    zIndex={2}
                    sx={{
                      animation: `riseUp ${e.duration} ease-out ${e.delay} infinite`,
                    }}
                  >
                    {e.emoji}
                  </Text>
                ))}
              </>
            )}
            <Box py={{ base: 10, md: 14 }} px={{ base: 6, md: 10 }} position="relative" zIndex={3}>
              <VStack spacing={6}>

                {/* Personalized Greeting */}
                <VStack spacing={2}>
                  <Text
                    fontSize="sm"
                    textTransform="uppercase"
                    letterSpacing="0.3em"
                    color={textSecondary}
                  >
                    {partyTimer.isPartyTime ? "OI" : "Hey"}
                  </Text>
                  <Heading
                    as="h1"
                    size={{ base: "xl", md: "2xl" }}
                    color={accentColor}
                    textAlign="center"
                    fontWeight="300"
                  >
                    {guest.firstName}!
                  </Heading>
                </VStack>

                {/* Photo */}
                <Circle
                  size={{ base: "120px", md: "150px" }}
                  overflow="hidden"
                  border="3px solid"
                  borderColor={partyTimer.isPartyTime ? "red.400" : accentColor}
                  bg={colorMode === 'light' ? "gray.100" : "gray.700"}
                  sx={partyTimer.isPartyTime ? { animation: 'bounce 2s ease-in-out infinite, sirenGlow 1.5s ease-in-out infinite' } : {}}
                >
                  <Image
                    src="/bday/truck_photo.png"
                    alt="Tom"
                    objectFit="cover"
                    w="100%"
                    h="100%"
                  />
                </Circle>

                {/* The Big Number */}
                <VStack spacing={1}>
                  <Text
                    fontSize="sm"
                    fontStyle="italic"
                    color={partyTimer.isPartyTime ? "red.500" : textSecondary}
                    fontWeight={partyTimer.isPartyTime ? "bold" : "normal"}
                  >
                    {partyTimer.isPartyTime ? "BREAKING: We are currently roasting" : "You're invited to celebrate"}
                  </Text>
                  <Text
                    fontSize={{ base: "5xl", md: "6xl" }}
                    fontWeight={partyTimer.isPartyTime ? "800" : "200"}
                    color={accentColor}
                    lineHeight="1"
                    sx={partyTimer.isPartyTime ? {
                      animation: 'spin40 3s ease-in-out infinite, rainbowText 4s linear infinite',
                      display: 'inline-block',
                    } : {}}
                  >
                    40
                  </Text>
                  <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    letterSpacing="0.3em"
                    color={textSecondary}
                    sx={partyTimer.isPartyTime ? { animation: 'rainbowText 3s linear infinite' } : {}}
                  >
                    {partyTimer.isPartyTime ? "Years of Questionable Decisions" : "Years Young"}
                  </Text>
                </VStack>

                <Divider w="60px" borderColor={accentColor} opacity={0.5} />

                {/* Event Details */}
                <VStack spacing={1}>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="300"
                    color={textPrimary}
                  >
                    Saturday 14th March 2026
                  </Text>
                  <Text fontSize="md" color={partyTimer.isPartyTime ? "red.500" : textSecondary} fontWeight={partyTimer.isPartyTime ? "bold" : "normal"}>
                    {partyTimer.isPartyTime ? "⚠️ IN PROGRESS — SEND BACKUP ⚠️" : "2pm to 6pm"}
                  </Text>
                  <Text fontSize="md" color={textSecondary}>
                    11 Athlone Street, Woolloongabba
                  </Text>
                </VStack>

                {/* Event Description */}
                <Text
                  fontSize="md"
                  color={partyTimer.isPartyTime ? "red.500" : textSecondary}
                  textAlign="center"
                  maxW="280px"
                  lineHeight="1.6"
                  fontWeight={partyTimer.isPartyTime ? "600" : "400"}
                >
                  {partyTimer.isPartyTime
                    ? "Tom's officially old and the party is LIVE! Get here before the food's gone!"
                    : "Join us for a relaxed poolside afternoon with drinks, BBQ, and good times."
                  }
                </Text>

                {/* WARNING ALERT during party mode */}
                {partyTimer.isPartyTime && (
                  <Box
                    bg="red.50"
                    border="2px solid"
                    borderColor="red.400"
                    borderRadius="lg"
                    px={4}
                    py={3}
                    w="full"
                    maxW="340px"
                    textAlign="center"
                    sx={{ animation: 'sirenGlow 1.5s ease-in-out infinite' }}
                  >
                    <Text fontSize="lg" fontWeight="900" color="red.600" mb={1}>
                      {i < 0.3 ? "⚠️ SITUATION CRITICAL ⚠️"
                        : i < 0.6 ? "🚨 SITUATION DETERIORATING 🚨"
                        : i < 0.85 ? "💀 SITUATION: UNRECOVERABLE 💀"
                        : "☠️ CODE BLACK — ALL HOPE IS LOST ☠️"}
                    </Text>
                    <Text fontSize="xs" color="red.500" fontWeight="600">
                      {i < 0.3
                        ? "A local man has turned 40. This is not a drill. He now makes a noise when he sits down. His spotify is 90% dad rock. He said \"back in my day\" unironically last week."
                        : i < 0.6
                        ? "UPDATE: Subject has been observed adjusting his lower back. Witnesses report he called a 25-year-old \"kiddo\". His phone font size is now at maximum. We are losing him."
                        : i < 0.85
                        ? "URGENT: Tom has started telling people about his lawnmower. He fell asleep on the couch at his own party. He referred to current music as \"noise\". His transition to full dad is 87% complete."
                        : "FINAL UPDATE: Tom has become his father. He checked the thermostat 4 times. He's wearing socks with sandals. He has opinions about guttering. There is nothing more we can do. Godspeed."}
                    </Text>
                  </Box>
                )}

                {/* Countdown / Party Timer */}
                <Box
                  py={4}
                  px={{ base: 4, md: 6 }}
                  borderRadius="2xl"
                  bg={partyTimer.isPartyTime
                    ? (colorMode === 'light' ? 'rgba(229, 62, 62, 0.08)' : 'rgba(229, 62, 62, 0.15)')
                    : (colorMode === 'light' ? `${champagne}30` : 'rgba(212, 175, 55, 0.1)')
                  }
                  border={partyTimer.isPartyTime ? "2px solid" : "1px solid"}
                  borderColor={partyTimer.isPartyTime ? 'red.400' : `${accentColor}40`}
                  w="full"
                  maxW="340px"
                  transition="all 0.5s ease"
                  sx={partyTimer.isPartyTime ? { animation: 'warningPulse 2s ease-in-out infinite' } : {}}
                >
                  {partyTimer.isPartyTime ? (
                    <>
                      <Text
                        fontSize="md"
                        fontWeight="bold"
                        textAlign="center"
                        mb={1}
                        sx={{ animation: 'rainbowText 3s linear infinite, bounce 1.5s ease-in-out infinite' }}
                      >
                        {getPartyMilestone(partyTimer.hours, partyTimer.minutes)}
                      </Text>
                      <Text
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="0.2em"
                        color="red.500"
                        fontWeight="bold"
                        textAlign="center"
                        mb={3}
                      >
                        🚨 Time Since Youth Departed 🚨
                      </Text>
                    </>
                  ) : (
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.2em"
                      color={textSecondary}
                      textAlign="center"
                      mb={3}
                    >
                      Countdown to Lift Off
                    </Text>
                  )}
                  <SimpleGrid columns={partyTimer.isPartyTime && partyTimer.days === 0 ? 3 : 4} spacing={{ base: 2, md: 4 }}>
                    {(partyTimer.isPartyTime ? [
                      ...(partyTimer.days > 0 ? [{ value: partyTimer.days, label: 'Days' }] : []),
                      { value: partyTimer.hours, label: 'Hours' },
                      { value: partyTimer.minutes, label: 'Mins' },
                      { value: partyTimer.seconds, label: 'Secs' },
                    ] : [
                      { value: partyTimer.days, label: 'Days' },
                      { value: partyTimer.hours, label: 'Hours' },
                      { value: partyTimer.minutes, label: 'Mins' },
                      { value: partyTimer.seconds, label: 'Secs' },
                    ]).map((item, index) => (
                      <VStack key={index} spacing={0}>
                        <Box
                          bg={colorMode === 'light' ? 'white' : 'rgba(255,255,255,0.1)'}
                          borderRadius="lg"
                          px={{ base: 2, md: 3 }}
                          py={{ base: 2, md: 3 }}
                          minW={{ base: "50px", md: "60px" }}
                          shadow="sm"
                          border="1px solid"
                          borderColor={partyTimer.isPartyTime ? 'red.200' : `${accentColor}30`}
                          transition="all 0.5s ease"
                        >
                          <Text
                            fontSize={{ base: "xl", md: "2xl" }}
                            color={partyTimer.isPartyTime
                              ? (colorMode === 'light' ? 'red.600' : 'red.300')
                              : accentColor
                            }
                            textAlign="center"
                            fontFamily="mono"
                            fontWeight={partyTimer.isPartyTime ? "700" : "300"}
                          >
                            {String(item.value).padStart(2, '0')}
                          </Text>
                        </Box>
                        <Text
                          fontSize={{ base: "2xs", md: "xs" }}
                          color={textSecondary}
                          textTransform="uppercase"
                          letterSpacing="0.1em"
                          mt={1}
                        >
                          {item.label}
                        </Text>
                      </VStack>
                    ))}
                  </SimpleGrid>
                </Box>

                <Divider w="60px" borderColor={accentColor} opacity={0.5} />

                {/* RSVP Section */}
                {hasSubmitted && selectedStatus ? (
                  // Already responded view
                  <VStack spacing={4} py={4}>
                    <Text
                      fontSize="sm"
                      textTransform="uppercase"
                      letterSpacing="0.2em"
                      color={textSecondary}
                    >
                      Your Response
                    </Text>
                    <Box
                      bg={selectedStatus === "ATTENDING" ? "green.100" : selectedStatus === "NOT_ATTENDING" ? "red.100" : "yellow.100"}
                      color={selectedStatus === "ATTENDING" ? "green.700" : selectedStatus === "NOT_ATTENDING" ? "red.700" : "yellow.700"}
                      px={6}
                      py={3}
                      borderRadius="full"
                      fontWeight="500"
                    >
                      {selectedStatus === "ATTENDING" && `Yes! ${plusOnes > 0 ? `+${plusOnes}` : ""}`}
                      {selectedStatus === "NOT_ATTENDING" && "Can't make it"}
                      {selectedStatus === "MAYBE" && "Maybe"}
                    </Box>
                    <Button
                      variant="ghost"
                      size="sm"
                      color={textSecondary}
                      onClick={() => setHasSubmitted(false)}
                    >
                      Change response
                    </Button>
                  </VStack>
                ) : (
                  // RSVP form
                  <VStack spacing={6} py={4} w="full">
                    <Text
                      fontSize="sm"
                      textTransform="uppercase"
                      letterSpacing="0.2em"
                      color={textSecondary}
                    >
                      Will you be joining us?
                    </Text>

                    <Flex
                      direction={{ base: "column", sm: "row" }}
                      gap={3}
                      w="full"
                      justify="center"
                      align="center"
                    >
                      <Button
                        size="lg"
                        variant={selectedStatus === "ATTENDING" ? "solid" : "outline"}
                        colorScheme="green"
                        onClick={() => setSelectedStatus("ATTENDING")}
                        borderRadius="full"
                        px={8}
                        w={{ base: "full", sm: "auto" }}
                        maxW={{ base: "200px", sm: "none" }}
                        animation={selectedStatus !== "ATTENDING" ? "gentleBounce 2s ease-in-out infinite" : "none"}
                        _hover={{
                          animation: "none",
                          transform: "translateY(-2px)",
                        }}
                      >
                        Yes!
                      </Button>
                      <Button
                        size="lg"
                        variant={selectedStatus === "NOT_ATTENDING" ? "solid" : "outline"}
                        colorScheme="red"
                        onClick={() => setSelectedStatus("NOT_ATTENDING")}
                        borderRadius="full"
                        px={8}
                        w={{ base: "full", sm: "auto" }}
                        maxW={{ base: "200px", sm: "none" }}
                      >
                        Can't make it
                      </Button>
                      <Button
                        size="lg"
                        variant={selectedStatus === "MAYBE" ? "solid" : "outline"}
                        colorScheme="yellow"
                        onClick={() => setSelectedStatus("MAYBE")}
                        borderRadius="full"
                        px={8}
                        w={{ base: "full", sm: "auto" }}
                        maxW={{ base: "200px", sm: "none" }}
                      >
                        Maybe
                      </Button>
                    </Flex>

                    {/* Plus ones - only show if attending */}
                    {selectedStatus === "ATTENDING" && (
                      <VStack spacing={3}>
                        <Text fontSize="sm" color={textSecondary}>
                          Bringing anyone?
                        </Text>
                        <Flex gap={2} wrap="wrap" justify="center">
                          {[0, 1, 2, 3].map((num) => (
                            <Button
                              key={num}
                              size={{ base: "sm", md: "md" }}
                              variant={plusOnes === num ? "solid" : "outline"}
                              bg={plusOnes === num ? accentColor : "transparent"}
                              color={plusOnes === num ? "white" : textSecondary}
                              borderColor={accentColor}
                              onClick={() => setPlusOnes(num)}
                              borderRadius="full"
                              minW={{ base: "60px", md: "70px" }}
                              _hover={{
                                bg: plusOnes === num ? accentColor : `${accentColor}20`,
                              }}
                            >
                              {num === 0 ? "Just me" : `+${num}`}
                            </Button>
                          ))}
                        </Flex>
                      </VStack>
                    )}

                    {/* Submit button */}
                    {selectedStatus && (
                      <Button
                        size="lg"
                        bg={accentColor}
                        color="white"
                        onClick={handleSubmit}
                        isLoading={submitting}
                        borderRadius="full"
                        px={10}
                        _hover={{
                          bg: colorMode === 'light' ? "#c9a030" : "#d4c090",
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.2s"
                      >
                        Submit RSVP
                      </Button>
                    )}
                  </VStack>
                )}

              </VStack>
            </Box>
            </Box>
          </Box>

          {/* Footer */}
          <HStack justify="center" pt={6} spacing={4} opacity={0.6}>
            {partyTimer.isPartyTime ? (
              <>
                <Text fontSize="lg">🎉</Text>
                <Text fontSize="lg">🍻</Text>
                <Text fontSize="lg">🔥</Text>
                <Text fontSize="lg">🥳</Text>
              </>
            ) : (
              <>
                <Text fontSize="lg">🏊</Text>
                <Text fontSize="lg">☀️</Text>
                <Text fontSize="lg">👨‍👩‍👧‍👦</Text>
                <Text fontSize="lg">🥂</Text>
              </>
            )}
          </HStack>
          <Text
            textAlign="center"
            fontSize="xs"
            color={textSecondary}
            pt={1}
            letterSpacing="0.1em"
            textTransform="uppercase"
          >
            {partyTimer.isPartyTime
              ? "This has been an emergency broadcast · Tom is old now · Pray for his knees"
              : "Family welcome · No pressure to swim"
            }
          </Text>

        </VStack>
      </Container>
    </Box>
  );
};

export default BdayRsvpPage;
